import { Typography } from '@euler/app/components/typography';
import { SiteIcon } from '@euler/app/flows/task/components/SiteIcon';
import { useDashboardInspectionTemplate } from '@euler/app/flows/task/pages/pre-inspection/functions/useDashboardInspectionFlow';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Center } from '@euler/components/Center';
import { Colors } from '@euler/components/Colors';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { InventoryQuery } from '@euler/functions/useInventory';
import { InspectionTemplateConf } from '@euler/model';
import {
  AbnormalLevel,
  InspectionCategory,
  InspectionTemplateSceneType,
} from '@euler/model/enum';
import { SiteInspection, TaskDetail } from '@euler/model/task-detail';
import { comparerDesc, isNotNull } from '@euler/utils';
import { array2map } from '@euler/utils/array';
import { uniq } from 'ramda';
import { memo, useMemo } from 'react';
import { FlatList, StyleProp, View, ViewStyle } from 'react-native';

type TroubleLightItem = {
  siteId: number;
  name: string;
  icon?: string;
  on?: boolean;
};

const Cell = memo(
  ({
    item,
    last,
    style,
  }: {
    item: TroubleLightItem;
    style?: StyleProp<ViewStyle>;
    first?: boolean;
    last?: boolean;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const badgeTheme = theme.task.preInspection.troubleLight.badge;
    return (
      <HStack
        justifyContent="space-between"
        css={`
          padding: ${rpx(8)}px 0;
          border-bottom-width: ${last ? 0 : 1}px;
          border-bottom-color: ${theme.panel.listItemSeparatorColor};
        `}
        style={style}
      >
        <HStack justifyContent="flex-start">
          <SiteIcon
            url={item.icon}
            type={
              item.icon
                ? item.icon.includes('.svg')
                  ? 'svg'
                  : 'image'
                : undefined
            }
            size={rpx(25)}
            color={Colors.White4}
            css={`
              margin-right: ${rpx(12)}px;
            `}
          />
          <Typography.Label
            regular
            size={rpx(22)}
            color={theme.task.preInspection.troubleLight.textColor}
            numberOfLines={1}
          >
            {item.name}
          </Typography.Label>
        </HStack>
        <Center
          css={`
            margin-left: ${rpx(8)}px;
            background-color: ${item.on
              ? badgeTheme.activeBgColor
              : badgeTheme.bgColor};
            border-width: 1px;
            border-color: ${item.on
              ? badgeTheme.activeBorderColor
              : badgeTheme.borderColor};
            padding: 0 ${rpx(9)}px;
            border-radius: ${rpx(4)}px;
          `}
        >
          <Typography.Label
            regular
            size={rpx(16)}
            color={item.on ? badgeTheme.activeTextColor : badgeTheme.textColor}
          >
            {item.on ? '已点亮' : '未点亮'}
          </Typography.Label>
        </Center>
      </HStack>
    );
  },
);

export const TroubleLightsView = memo(
  ({
    items,
    style,
  }: {
    items: TroubleLightItem[];
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <View style={style}>
        <Typography.Label
          bold
          color={theme.panel.titleTextColor}
          size={rpx(32)}
          css={`
            margin-bottom: ${rpx(20)}px;
          `}
        >
          仪表故障灯
        </Typography.Label>
        <FlatList
          data={items}
          keyExtractor={x => String(x.siteId)}
          renderItem={({ item, index }) => (
            <Cell
              item={item}
              first={index === 0}
              last={index === items.length - 1}
            />
          )}
          css={`
            flex: 1;
          `}
        />
      </View>
    );
  },
);

const troubleLightItemsFromTemplateConf = (
  conf: InspectionTemplateConf,
  inspectedSites: SiteInspection[] | undefined,
  inventoryQuery: InventoryQuery,
): TroubleLightItem[] => {
  const inspectedSiteMap = array2map(inspectedSites ?? [], x => x.siteId);
  const categories = conf.categories;
  const selectedSiteIds = categories.flatMap(x =>
    x.groups.flatMap(g => g.siteIds),
  );
  const disabledSiteIdSet = new Set(
    categories.flatMap(x => x.groups.flatMap(g => g.disabledSiteIds ?? [])),
  );
  return uniq(selectedSiteIds.filter(x => !disabledSiteIdSet.has(x)))
    .map<TroubleLightItem | null>(siteId => {
      const site = inventoryQuery.getSiteById(siteId);
      if (!site) return null;
      const inspectedSite = inspectedSiteMap.get(siteId);
      return {
        siteId,
        name: site.name,
        icon: site.iconUrl,
        on:
          inspectedSite != null &&
          inspectedSite.abnormalLevel !== AbnormalLevel.Fine,
      };
    })
    .filter(isNotNull)
    .sort((x, y) => comparerDesc(x.on ? 1 : 0, y.on ? 1 : 0));
};

export const WiredTroubleLightsView = memo(
  ({
    detail,
    inventoryQuery,
    style,
  }: {
    detail: TaskDetail;
    inventoryQuery: InventoryQuery;
    style?: StyleProp<ViewStyle>;
  }) => {
    const template = useDashboardInspectionTemplate();
    const troubleLightsItems = useMemo(() => {
      const flow = detail.preInspection.flows.find(
        x =>
          x.category === InspectionCategory.Pre &&
          x.template.sceneType === InspectionTemplateSceneType.Dashboard,
      );
      if (flow == null) {
        if (template == null) return [];
        return troubleLightItemsFromTemplateConf(
          template.conf,
          detail.preInspection.sites,
          inventoryQuery,
        );
      }
      return troubleLightItemsFromTemplateConf(
        flow.template.conf,
        detail.preInspection.sites,
        inventoryQuery,
      );
    }, [
      detail.preInspection.flows,
      detail.preInspection.sites,
      inventoryQuery,
      template,
    ]);
    return <TroubleLightsView items={troubleLightsItems} style={style} />;
  },
);
